import { createClient } from '@/lib/supabase/client'
import type { Budget, CategoryBudgetWithSpending } from '@/lib/types'
import { getCategoryIcon, getCategoryColor } from '@/lib/utils/category-helpers'

/**
 * Fetch all budgets for a user
 */
export async function fetchUserBudgets(userId: string): Promise<Budget[]> {
  const supabase = createClient()

  const { data, error } = await supabase
    .from('budgets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Error fetching budgets:', error)
    throw new Error('Failed to fetch budgets')
  }

  // Transform database records to application types
  return data.map((budget) => ({
    id: budget.id,
    userId: budget.user_id,
    category: budget.category,
    amount: budget.amount,
    period: budget.period as 'weekly' | 'monthly' | 'yearly',
    startDate: budget.start_date,
    subAccountId: (budget as any).sub_account_id,
    createdAt: budget.created_at,
    updatedAt: budget.updated_at,
  }))
}

/**
 * Create or update a budget for a category
 */
export async function upsertCategoryBudget(
  userId: string,
  category: string,
  amount: number,
  period: 'weekly' | 'monthly' | 'yearly' = 'monthly',
  subAccountId?: string | null
): Promise<Budget> {
  const supabase = createClient()

  // Check if budget already exists for this category and sub-account
  let query: any = supabase
    .from('budgets')
    .select('*')
    .eq('user_id', userId)
    .eq('category', category)

  // Filter by sub_account_id (including NULL values if subAccountId is not provided)
  if (subAccountId) {
    query.eq('sub_account_id', subAccountId)
  } else {
    query.is('sub_account_id', null)
  }

  const { data: existing } = await query.single()

  if (existing) {
    // Update existing budget
    const { data, error } = await supabase
      .from('budgets')
      .update({
        amount,
        period,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id)
      .select()
      .single()

    if (error) {
      console.error('Error updating budget:', error)
      throw new Error('Failed to update budget')
    }

    return {
      id: data.id,
      userId: data.user_id,
      category: data.category,
      amount: data.amount,
      period: data.period as 'weekly' | 'monthly' | 'yearly',
      startDate: data.start_date,
      subAccountId: (data as any).sub_account_id,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    }
  } else {
    // Create new budget
    const { data, error } = await supabase
      .from('budgets')
      .insert({
        user_id: userId,
        category,
        amount,
        period,
        start_date: new Date().toISOString().split('T')[0],
        sub_account_id: subAccountId,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating budget:', error)
      throw new Error('Failed to create budget')
    }

    return {
      id: data.id,
      userId: data.user_id,
      category: data.category,
      amount: data.amount,
      period: data.period as 'weekly' | 'monthly' | 'yearly',
      startDate: data.start_date,
      subAccountId: (data as any).sub_account_id,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    }
  }
}

/**
 * Delete a budget
 */
export async function deleteBudget(budgetId: string): Promise<void> {
  const supabase = createClient()

  const { error } = await supabase
    .from('budgets')
    .delete()
    .eq('id', budgetId)

  if (error) {
    console.error('Error deleting budget:', error)
    throw new Error('Failed to delete budget')
  }
}

/**
 * Calculate spending for a specific category in the current month
 */
export async function calculateCategorySpending(
  userId: string,
  category: string,
  subAccountId?: string | null
): Promise<number> {
  const supabase = createClient()

  // Get start and end of current month
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)

  const query = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)
    .eq('category', category)
    .gte('created_at', startOfMonth.toISOString())
    .lte('created_at', endOfMonth.toISOString())

  // Filter by sub_account_id if provided
  if (subAccountId) {
    query.eq('sub_account_id', subAccountId)
  } else if (subAccountId === null) {
    query.is('sub_account_id', null)
  }
  // If subAccountId is undefined, don't filter by sub_account_id (show all)

  const { data, error } = await query

  if (error) {
    console.error('Error calculating spending:', error)
    return 0
  }

  return data.reduce((total, expense) => total + expense.amount, 0)
}

/**
 * Get budget with spending data for all categories
 * @param userId - User ID
 * @param subAccountId - Optional sub-account filter (undefined = all accounts, null = global budgets only, string = specific account)
 */
export async function getBudgetsWithSpending(
  userId: string,
  subAccountId?: string | null
): Promise<CategoryBudgetWithSpending[]> {
  const budgets = await fetchUserBudgets(userId)

  // Filter budgets by sub-account if specified
  const filteredBudgets = budgets.filter((budget) => {
    if (subAccountId === undefined) {
      // No filter - return all budgets
      return true
    } else if (subAccountId === null) {
      // Only global budgets (not assigned to any account)
      return budget.subAccountId === null
    } else {
      // Specific account only
      return budget.subAccountId === subAccountId
    }
  })

  const budgetsWithSpending = await Promise.all(
    filteredBudgets.map(async (budget) => {
      const spent = await calculateCategorySpending(userId, budget.category, budget.subAccountId)
      const remaining = budget.amount - spent
      const percentage = budget.amount > 0 ? (spent / budget.amount) * 100 : 0

      return {
        category: budget.category,
        budgetAmount: budget.amount,
        spent,
        remaining,
        percentage: Math.min(percentage, 100),
        icon: getCategoryIcon(budget.category),
        color: getCategoryColor(budget.category),
        period: budget.period,
        subAccountId: budget.subAccountId,
      }
    })
  )

  return budgetsWithSpending
}
